/**
 * components/wizard/ClickGuidanceDiagram.jsx — Phase E v8c.13b (2026-02-28).
 *
 * Renders an inline SVG micro-blueprint (~80×40 px) of a calibration
 * primitive next to the textual guidance in `AddZonePanel`. Purely
 * presentational — consumes the `diagram` descriptor from
 * `lib/clickGuidance.js::clickGuidanceFor` and never touches any
 * state hook or event stream.
 *
 * Why a separate component?
 * ─────────────────────────
 * Keeps `clickGuidance.js` pure-data + testable, and lets the SVG
 * stay declarative + style-aligned with the wizard's amber accent
 * (so a future colour-token migration only touches THIS file).
 *
 * Visual conventions:
 *   • field outline: light rounded rectangle (10 % opacity stroke)
 *   • field reference geometry (goal-lines, brick-band, midline)
 *     drawn at 25 % opacity as a faint backdrop hint
 *   • numbered click targets: amber-filled circles with the digit
 *     centred in black text, ≈12 % of viewBox width
 *   • directional arrows: amber stroke, 1.5 px width, terminated
 *     with a small filled triangle arrowhead
 *   • polygon arrow loops: arrow per edge (1→2→3→4→1)
 */
import React from "react";

const VB_W = 100;
const VB_H = 50;

const FAR_LEFT = 6, MID_LEFT = 22, CENTER = 50, MID_RIGHT = 78, FAR_RIGHT = 94;
const TOP = 12, BOT = 38;

const POINT_R = 4;
const ACCENT = "#fbbf24";       // amber-400 — matches the panel border
const ACCENT_DIM = "rgba(251,191,36,0.4)";
const FIELD_STROKE = "rgba(251,191,36,0.18)";
const REF_STROKE = "rgba(251,191,36,0.32)";

/** Faint reference geometry under the numbered points. */
function FieldHintLayer({ fieldHint }) {
  // Always render the outer field outline.
  const outline = (
    <rect
      x="2" y="6" width={VB_W - 4} height={VB_H - 12}
      rx="2" fill="none" stroke={FIELD_STROKE} strokeWidth="0.75"
    />
  );
  // Field reference markers per hint.
  let extra = null;
  if (fieldHint === "full" || fieldHint === "endzone-left" || fieldHint === "endzone-right"
      || fieldHint === "brick-band" || fieldHint === "midline"
      || fieldHint === "left-half" || fieldHint === "right-half") {
    extra = (
      <>
        {/* Goal lines (always drawn so the operator orients) */}
        <line x1={MID_LEFT} y1={TOP - 2} x2={MID_LEFT} y2={BOT + 2}
              stroke={REF_STROKE} strokeWidth="0.5" strokeDasharray="1 1" />
        <line x1={MID_RIGHT} y1={TOP - 2} x2={MID_RIGHT} y2={BOT + 2}
              stroke={REF_STROKE} strokeWidth="0.5" strokeDasharray="1 1" />
        {/* Endzone shading depending on hint */}
        {fieldHint === "endzone-left" && (
          <rect x="2" y={TOP - 2} width={MID_LEFT - 2} height={BOT - TOP + 4}
                fill={ACCENT} opacity="0.06" />
        )}
        {fieldHint === "endzone-right" && (
          <rect x={MID_RIGHT} y={TOP - 2} width={VB_W - 2 - MID_RIGHT} height={BOT - TOP + 4}
                fill={ACCENT} opacity="0.06" />
        )}
        {/* Brick marks (small × symbols 8 m past each goal line) */}
        {(fieldHint === "brick-band" || fieldHint === "full") && (
          <>
            <text x={MID_LEFT + 8} y="27" fontSize="3.5"
                  fill={REF_STROKE} textAnchor="middle">×</text>
            <text x={MID_RIGHT - 8} y="27" fontSize="3.5"
                  fill={REF_STROKE} textAnchor="middle">×</text>
          </>
        )}
        {/* Midline */}
        {(fieldHint === "midline" || fieldHint === "left-half" || fieldHint === "right-half") && (
          <line x1={CENTER} y1={TOP - 2} x2={CENTER} y2={BOT + 2}
                stroke={REF_STROKE} strokeWidth="0.5" strokeDasharray="1 1" />
        )}
      </>
    );
  }
  return <>{outline}{extra}</>;
}

/** A small filled triangle arrowhead at the END of a line segment. */
function Arrowhead({ x1, y1, x2, y2 }) {
  // Direction vector
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) return null;
  const ux = dx / len;
  const uy = dy / len;
  // Arrowhead size in viewBox units.
  const HEAD = 3;
  // Pull the head back so it doesn't overlap the destination circle.
  const tipX = x2 - ux * (POINT_R + 0.5);
  const tipY = y2 - uy * (POINT_R + 0.5);
  // Perpendicular vector for the two base corners.
  const px = -uy;
  const py = ux;
  const baseX = tipX - ux * HEAD;
  const baseY = tipY - uy * HEAD;
  const aX = baseX + px * (HEAD * 0.55);
  const aY = baseY + py * (HEAD * 0.55);
  const bX = baseX - px * (HEAD * 0.55);
  const bY = baseY - py * (HEAD * 0.55);
  return (
    <polygon
      points={`${tipX},${tipY} ${aX},${aY} ${bX},${bY}`}
      fill={ACCENT}
    />
  );
}

/** Connecting line + arrowhead between two consecutive points. */
function ArrowSegment({ from, to }) {
  // Trim the visible line so it doesn't disappear under the circle.
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) return null;
  const ux = dx / len;
  const uy = dy / len;
  const x1 = from.x + ux * (POINT_R + 0.5);
  const y1 = from.y + uy * (POINT_R + 0.5);
  const x2 = to.x - ux * (POINT_R + 0.5);
  const y2 = to.y - uy * (POINT_R + 0.5);
  return (
    <>
      <line x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={ACCENT_DIM} strokeWidth="1.2" strokeLinecap="round" />
      <Arrowhead x1={from.x} y1={from.y} x2={to.x} y2={to.y} />
    </>
  );
}

/** Numbered click target — circle with the digit centred. */
function PointMarker({ x, y, label }) {
  return (
    <g>
      <circle cx={x} cy={y} r={POINT_R}
              fill={ACCENT} stroke="black" strokeWidth="0.5" />
      <text
        x={x} y={y + 1.4}
        fontSize="5.2"
        fontWeight="900"
        fill="black"
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        {label}
      </text>
    </g>
  );
}

export default function ClickGuidanceDiagram({ diagram }) {
  if (!diagram || !Array.isArray(diagram.points) || !diagram.points.length) {
    return null;
  }
  const { kind, fieldHint, points } = diagram;

  // Build the arrow path. For lines / point — just chain through the
  // listed points. For polygons — chain 1→2→3→4 AND close 4→1 so the
  // clockwise loop is visually unambiguous.
  const arrows = [];
  for (let i = 0; i < points.length - 1; i++) {
    arrows.push(<ArrowSegment key={`a${i}`} from={points[i]} to={points[i + 1]} />);
  }
  if (kind === "polygon" && points.length >= 3) {
    arrows.push(
      <ArrowSegment
        key="aclose"
        from={points[points.length - 1]}
        to={points[0]}
      />,
    );
  }

  // For the single-point "DoppelKlick" diagram we render a faint
  // pulsing concentric ring around the marker so it visually reads
  // as "click here twice" rather than "one of many".
  const isDoubleClickPoint = kind === "point";

  return (
    <svg
      data-testid="cal-add-zone-guidance-diagram"
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      preserveAspectRatio="xMidYMid meet"
      className="w-[120px] h-[60px] flex-shrink-0"
      role="img"
      aria-label={`Klick-Diagramm · ${kind}`}
    >
      <FieldHintLayer fieldHint={fieldHint} />
      {arrows}
      {points.map((p, i) => (
        <React.Fragment key={`p${i}`}>
          {isDoubleClickPoint && (
            <circle cx={p.x} cy={p.y} r={POINT_R + 2.5}
                    fill="none" stroke={ACCENT_DIM}
                    strokeWidth="0.6" strokeDasharray="1 1" />
          )}
          <PointMarker x={p.x} y={p.y} label={p.label} />
        </React.Fragment>
      ))}
    </svg>
  );
}
